/**
 * SSE frame parser over `fetch` + `ReadableStream`.
 *
 * `EventSource` is not an option: it only does GET and hands back no headers,
 * while a run starts as a POST with a body. The side benefit is `AbortController`,
 * which is what the «Стоп» button is made of.
 *
 * ── WHAT THE WIRE ACTUALLY LOOKS LIKE ────────────────────────────────────────
 * Measured 2026-08-28 against the guide's dev instance on new-gigachat-01
 * (`localhost:8051`, `assistant_id: peterhof_agent`, one turn that ran
 * `search_knowledge` + `object_info` + `suggest_chips`). Two readings of reagent
 * disagreed about all of this, so it was settled by curl rather than by voting:
 *
 *  1. **Every frame carries `id:`** — `{run_id}_event_{seq}`, numbered from 1.
 *     Replay is therefore real, and §3.5's recovery ladder stands as designed.
 *  2. **`Last-Event-ID` works and takes the FULL id.** `-1` replays from the
 *     start (378 frames of 378); `…_event_370` replayed exactly frames 371-378.
 *     The response also advertises `access-control-allow-headers: Last-Event-ID`.
 *  3. **`values` is FLAT** — `data.attachments`, `data.messages`, `data.actions`,
 *     `data.visitor`, `turn_text`, `guide_line`, `geo`, `deadline_at`,
 *     `loop_break`. There is no `chunk` wrapper. (The tolerant read is kept for
 *     `custom` only, where nothing could be observed yet — see 6.)
 *  4. **THERE IS NO `end` EVENT, and no `error` event either.** The stream simply
 *     closes. `grep '^event:'` over a whole run yields `metadata`(1),
 *     `values`(8), `messages/metadata`(6), `messages/partial`(360),
 *     `messages/complete`(3) — and the replay of the stored log returns the same
 *     378 frames, so this is the event store's content and not a truncation.
 *     **A clean close is therefore the end of the run**, and a run's verdict is
 *     read from `GET /threads/{tid}/runs/{rid}` when it matters. Both `end` and
 *     `error` are still handled: a newer reagent may add them, and handling a
 *     frame that never comes costs one branch.
 *  5. **AI text arrives ONLY as `messages/partial` (cumulative `[message]`)**,
 *     tool results as `messages/complete`. Not one delta-shaped `messages` frame
 *     appeared in the whole run — but the tolerant branch stays, because the
 *     graph decides that, not the transport.
 *  6. **`custom` could not be observed**: `peterhof_agent` emits no `dh_ui` yet.
 *     `stream_mode: ["messages","custom","values","updates"]` is accepted without
 *     complaint, so both wrappings (`data.dh_ui` and `data.chunk.dh_ui`) stay
 *     readable until `peterhof_canvas` lands on dev.
 *
 * Errors BEFORE the stream are ordinary HTTP: an unknown `assistant_id` is a
 * 404 with `{"error":"not_found","message":"…"}`. A thread that was never created
 * is NOT an error — reagent made one implicitly on a fresh uuid — but we still
 * create it, idempotently, so the run has a thread record of its own.
 */

export type SseFrame = {
  /** `event:` — already stripped of the `|<namespace>` subgraph suffix. */
  event: string;
  /** Parsed `data:`; the raw text when it is not JSON. */
  data: unknown;
  /** `id:` — `{run_id}_event_{seq}`, or `null` when the frame carried none. */
  id: string | null;
};

/** Everything after `|` is the subgraph namespace (`custom|tools`, `values|x`). */
export function baseEventName(raw: string): string {
  const bar = raw.indexOf('|');
  return bar === -1 ? raw : raw.slice(0, bar);
}

/**
 * Incremental SSE frame decoder.
 *
 * Kept as a class with a `push`/`flush` pair rather than a generator so the unit
 * tests can feed it byte-boundary splits — a chunk that cuts a `data:` line in
 * half is the failure mode that only shows up on a slow network.
 */
export class SseDecoder {
  private buffer = '';
  private eventName = '';
  private dataLines: string[] = [];
  private lastId: string | null = null;

  /** Feeds one decoded text chunk, returns whatever frames completed inside it. */
  push(chunk: string): SseFrame[] {
    this.buffer += chunk;
    const frames: SseFrame[] = [];
    let nl: number;
    // Normalise CRLF as we go: a proxy may rewrite line endings.
    while ((nl = this.buffer.indexOf('\n')) !== -1) {
      const line = this.buffer.slice(0, nl).replace(/\r$/, '');
      this.buffer = this.buffer.slice(nl + 1);
      const frame = this.line(line);
      if (frame) frames.push(frame);
    }
    return frames;
  }

  /** Emits a frame the stream ended without a trailing blank line for. */
  flush(): SseFrame[] {
    const rest = this.buffer;
    this.buffer = '';
    const frames: SseFrame[] = [];
    for (const line of rest.split('\n')) {
      const frame = this.line(line.replace(/\r$/, ''));
      if (frame) frames.push(frame);
    }
    const tail = this.emit();
    if (tail) frames.push(tail);
    return frames;
  }

  /** The id of the newest frame seen — what a resume sends as `Last-Event-ID`. */
  get lastEventId(): string | null {
    return this.lastId;
  }

  private line(line: string): SseFrame | null {
    if (line === '') return this.emit();
    if (line.startsWith(':')) return null; // comment / keep-alive
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);

    if (field === 'event') this.eventName = value;
    else if (field === 'data') this.dataLines.push(value);
    else if (field === 'id') this.lastId = value;
    // `retry:` is accepted and ignored — our backoff is our own (§3.5).
    return null;
  }

  private emit(): SseFrame | null {
    if (this.eventName === '' && this.dataLines.length === 0) return null;
    const raw = this.dataLines.join('\n');
    this.dataLines = [];
    const event = baseEventName(this.eventName);
    this.eventName = '';
    let data: unknown = raw;
    if (raw !== '') {
      try {
        data = JSON.parse(raw);
      } catch {
        /* a non-JSON payload is handed over as text rather than dropped */
      }
    }
    return { event, data, id: this.lastId };
  }
}

export type SseStreamOptions = {
  signal?: AbortSignal;
  /** Called once with the `Response`, before the body is read. */
  onResponse?: (response: Response) => void;
  /** Silence that counts as a break; `0` switches the watchdog off. */
  idleMs?: number;
};

/**
 * How long a stream may say nothing before it is treated as broken.
 *
 * **A dead path does not always close the socket.** Live on dev (2026-09-01) the
 * tunnel under the run was killed mid-turn: the proxy held the connection, the
 * body's `read()` never resolved and never rejected, and `runTurn` waited for
 * ever — the visitor sat under «Думаю…» while the server finished the run and
 * wrote the whole answer. Nothing above the reader can see that, because from
 * up there a silent stream and a slow model look identical.
 *
 * The number is measured, not guessed: one route-building turn against dev
 * (3170 frames, 126 s) had two legitimate silences of 22.4 s and 24.9 s, both
 * in front of a `messages/metadata` — the model thinking before its first
 * token. 45 s is clear of that with room, and a FALSE break is cheap by
 * construction: the ladder resumes with `Last-Event-ID`, the run carries on
 * server-side under `on_disconnect: "continue"`, and only the frames actually
 * missed are replayed.
 */
export const SSE_IDLE_MS = 45_000;

/** A stream that went silent. Recoverable — the caller resumes rather than fails. */
export class SseIdleError extends Error {
  constructor(readonly idleMs: number) {
    super('sse-idle');
    this.name = 'SseIdleError';
  }
}

async function readWithin(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  idleMs: number,
): Promise<ReadableStreamReadResult<Uint8Array>> {
  if (!Number.isFinite(idleMs) || idleMs <= 0) return reader.read();
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      reader.read(),
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new SseIdleError(idleMs)), idleMs);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Reads an SSE response body and yields frames until the stream closes.
 *
 * `res.body` is missing on Safari < 14.1; the caller turns that into an honest
 * «обновите браузер» rather than silently degrading (R10 — the fallback route
 * `/runs/wait` is not exposed publicly, so there is nothing to degrade to).
 */
export async function* readSse(
  response: Response,
  options: SseStreamOptions = {},
): AsyncGenerator<SseFrame, void, void> {
  const body = response.body;
  if (!body) throw new StreamingUnsupportedError();
  const reader = body.getReader();
  const decoder = new TextDecoder('utf-8');
  const frames = new SseDecoder();
  const idleMs = options.idleMs ?? SSE_IDLE_MS;
  try {
    for (;;) {
      const { done, value } = await readWithin(reader, idleMs);
      if (done) break;
      for (const frame of frames.push(decoder.decode(value, { stream: true }))) yield frame;
    }
    for (const frame of frames.flush()) yield frame;
  } catch (error) {
    // The socket is still open on a silent break — drop it here, or the ladder's
    // resume would queue behind a connection nobody is going to write to.
    if (error instanceof SseIdleError) {
      try {
        await reader.cancel();
      } catch {
        /* already gone */
      }
    }
    throw error;
  } finally {
    // An aborted read leaves the reader locked; releasing it lets the retry
    // ladder open a new one on the same connection pool.
    try {
      reader.releaseLock();
    } catch {
      /* already released */
    }
  }
  void options.signal;
}

export class StreamingUnsupportedError extends Error {
  constructor() {
    super('streaming-unsupported');
    this.name = 'StreamingUnsupportedError';
  }
}
